import { Module } from '../../types';
import { regexModule2QuestionBank } from '../questionBanks/regexModule2Questions';

export const regexModule2: Module = {
  id: 'module2',
  title: 'Character Classes and Quantifiers',
  description:
    'Learn about predefined character classes (\\d, \\w, \\s), custom character classes with brackets, and quantifiers for controlling repetition.',
  duration: '3.5 hours',
  content: `# Character Classes and Quantifiers

## 🎯 Learning Objectives

By the end of this module, you will:
- Use predefined character classes (\\d, \\w, \\s) to match specific types of characters
- Create custom character classes with brackets [abc] for precise matching
- Apply range notation [a-z] and negation [^abc] for flexible patterns
- Master quantifiers (+, *, ?, {n}, {n,m}) to control repetition
- Combine character classes with quantifiers for powerful patterns
- Build practical patterns for emails, phone numbers, and data validation

## 📚 Introduction to Character Classes

### What Are Character Classes?

Character classes are shortcuts that match specific types of characters. Instead of writing [0123456789] to match any digit, you can simply use \\d. They make patterns more readable and efficient.

### Why Character Classes Matter

Consider these tasks:
- **Find all numbers** in a document: Without classes, you'd need [0123456789]+
- **Match any word character**: Without classes, you'd need [abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_]+
- **Find whitespace**: Without classes, you'd need [ \\t\\n\\r\\f\\v]+

Character classes solve these problems elegantly!

## 🔢 Predefined Character Classes

### \\d - Digits (0-9)

The \\d class matches any single digit from 0 to 9.

\`\`\`python
import re

text = "Room 101, Floor 3, Building A2"
pattern = r"\\d"

# Find all single digits
digits = re.findall(pattern, text)
print(f"Single digits: {digits}")
# Output: Single digits: ['1', '0', '1', '3', '2']

# Find multi-digit numbers
pattern = r"\\d+"
numbers = re.findall(pattern, text)
print(f"Numbers: {numbers}")
# Output: Numbers: ['101', '3', '2']
\`\`\`

### \\D - Non-digits (NOT 0-9)

The \\D class matches any character that is NOT a digit.

\`\`\`python
text = "Price: $29.99"
pattern = r"\\D+"

non_digits = re.findall(pattern, text)
print(f"Non-digits: {non_digits}")
# Output: Non-digits: ['Price: $', '.']
\`\`\`

### \\w - Word Characters (a-z, A-Z, 0-9, _)

The \\w class matches "word characters": letters, digits, and underscores.

\`\`\`python
text = "user_name123 and email@domain.com"
pattern = r"\\w+"

words = re.findall(pattern, text)
print(f"Word sequences: {words}")
# Output: Word sequences: ['user_name123', 'and', 'email', 'domain', 'com']
\`\`\`

### \\s - Whitespace Characters

The \\s class matches various whitespace characters: space, tab, newline, etc.

\`\`\`python
text = "Line 1\\n\\tIndented line\\r\\nAnother line"
pattern = r"\\s+"

# Replace multiple whitespace with single space
normalized = re.sub(pattern, " ", text)
print(f"Normalized: {repr(normalized)}")
\`\`\`

## 🎯 Custom Character Classes

### Basic Syntax: [abc]

Square brackets create custom character classes that match any ONE character from the set.

\`\`\`python
text = "I have cats, dogs, and bats as pets"
pattern = r"[cdb]ats"

matches = re.findall(pattern, text)
print(f"Matches: {matches}")
# Output: Matches: ['cats', 'bats']  # 'dogs' doesn't match the pattern
\`\`\`

### Character Ranges: [a-z]

Use hyphens to specify ranges of characters.

\`\`\`python
text = "Grades: A, B+, C-, D, F"
pattern = r"[A-F][+-]?"  # Grade letter optionally followed by + or -

grades = re.findall(pattern, text)
print(f"Grades: {grades}")
# Output: Grades: ['A', 'B+', 'C-', 'D', 'F']
\`\`\`

### Negated Character Classes: [^abc]

The caret (^) at the start of a character class negates it - matches anything NOT in the set.

\`\`\`python
text = "Hello123 World456!"

# Find non-alphanumeric characters
non_alphanum = re.findall(r"[^a-zA-Z0-9]+", text)
print(f"Non-alphanumeric: {non_alphanum}")
# Output: Non-alphanumeric: [' ', '!']

# Remove non-alphanumeric characters
cleaned = re.sub(r"[^a-zA-Z0-9]", "", text)
print(f"Cleaned: {cleaned}")
# Output: Cleaned: Hello123World456
\`\`\`

## ⚡ Quantifiers - Controlling Repetition

### The + Quantifier: One or More

The + quantifier matches one or more of the preceding element.

\`\`\`python
text = "I have 1 cat, 15 dogs, and 100 fish"
pattern = r"\\d+"  # One or more digits

numbers = re.findall(pattern, text)
print(f"Numbers: {numbers}")
# Output: Numbers: ['1', '15', '100']
\`\`\`

### The * Quantifier: Zero or More

The * quantifier matches zero or more of the preceding element.

\`\`\`python
text = "Colors: red, blue, green, yellow"
pattern = r"[a-z]+"  # One or more letters (no empty matches)

words = re.findall(pattern, text)
print(f"Words: {words}")
# Output: Words: ['olors', 'red', 'blue', 'green', 'yellow']
\`\`\`

### The ? Quantifier: Zero or One (Optional)

The ? quantifier makes the preceding element optional.

\`\`\`python
text = "Colors: color, colour, colors, colours"
pattern = r"colou?rs?"  # 'u' is optional, 's' is optional

matches = re.findall(pattern, text)
print(f"Matches: {matches}")
# Output: Matches: ['color', 'colour', 'colors', 'colours']
\`\`\`

### Exact Quantifiers: {n}

Use {n} to match exactly n occurrences.

\`\`\`python
text = "ZIP codes: 12345, 123456, 1234, 90210"
pattern = r"\\b\\d{5}\\b"  # Exactly 5 digits as whole word

zip_codes = re.findall(pattern, text)
print(f"Valid ZIP codes: {zip_codes}")
# Output: Valid ZIP codes: ['12345', '90210']
\`\`\`

### Range Quantifiers: {n,m}

Use {n,m} to match between n and m occurrences.

\`\`\`python
text = "Passwords: a, abc, abcdef, abcdefghijk"
pattern = r"\\b[a-z]{6,10}\\b"  # 6 to 10 lowercase letters

valid_passwords = re.findall(pattern, text)
print(f"Valid passwords (6-10 chars): {valid_passwords}")
# Output: Valid passwords (6-10 chars): ['abcdef']
\`\`\`

## 🔗 Combining Character Classes and Quantifiers

### Building Email Patterns

\`\`\`python
emails = "Contact: john.doe@company.com, jane_smith@university.edu"

# Simple email pattern
pattern = r"[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}"
valid_emails = re.findall(pattern, emails)
print(f"Valid emails: {valid_emails}")

# Breaking down the pattern:
# [a-zA-Z0-9._%+-]+  : Username part (letters, digits, special chars)
# @                   : Literal @ symbol
# [a-zA-Z0-9.-]+      : Domain name (letters, digits, dots, hyphens)
# \\.                 : Literal dot
# [a-zA-Z]{2,}        : Top-level domain (2+ letters)
\`\`\`

### Phone Number Patterns

\`\`\`python
phones = '''
Phone numbers:
(555) 123-4567
555-123-4567
555.123.4567
5551234567
'''

# Flexible phone pattern
pattern = r"\\(?\\d{3}\\)?[-\\s.]?\\d{3}[-\\s.]?\\d{4}"
phone_numbers = re.findall(pattern, phones)
print(f"Found {len(phone_numbers)} phone numbers")

# Breaking down the pattern:
# \\(?\\d{3}\\)?        : Area code, optionally in parentheses
# [-\\s.]?             : Optional separator (dash, space, or dot)
# \\d{3}               : Exchange (3 digits)
# [-\\s.]?             : Optional separator
# \\d{4}               : Number (4 digits)
\`\`\`

### Password Validation

\`\`\`python
def validate_password(password):
    '''
    Validate password requirements:
    - 8-20 characters
    - At least one uppercase letter
    - At least one lowercase letter  
    - At least one digit
    - At least one special character
    '''
    
    # Check length
    if not re.match(r"^.{8,20}$", password):
        return False, "Password must be 8-20 characters"
    
    # Check for uppercase
    if not re.search(r"[A-Z]", password):
        return False, "Password must contain uppercase letter"
    
    # Check for lowercase
    if not re.search(r"[a-z]", password):
        return False, "Password must contain lowercase letter"
    
    # Check for digit
    if not re.search(r"\\d", password):
        return False, "Password must contain a digit"
    
    # Check for special character
    if not re.search(r"[!@#$%^&*(),.?':{}|<>]", password):
        return False, "Password must contain special character"
    
    return True, "Password is valid"
\`\`\`

## ⚠️ Common Pitfalls and Solutions

### 1. Greedy Quantifiers

Quantifiers are greedy by default - they match as much as possible:

\`\`\`python
html = '<div>Content 1</div><div>Content 2</div>'

# Greedy - matches too much!
greedy = re.search(r'<div>.*</div>', html)
print(f"Greedy match: {greedy.group()}")
# Output: <div>Content 1</div><div>Content 2</div>

# Non-greedy - add ? after quantifier
non_greedy = re.search(r'<div>.*?</div>', html)
print(f"Non-greedy match: {non_greedy.group()}")
# Output: <div>Content 1</div>
\`\`\`

### 2. Character Class Mistakes

\`\`\`python
# WRONG: [a-Z] includes unwanted characters
text = "Hello World 123"
wrong = re.findall(r"[a-Z]+", text)
print(f"Wrong [a-Z]: {wrong}")  # Includes unwanted chars

# CORRECT: [a-zA-Z] for all letters
correct = re.findall(r"[a-zA-Z]+", text)
print(f"Correct [a-zA-Z]: {correct}")
# Output: ['Hello', 'World']
\`\`\`

## 🏆 Best Practices

### 1. Be Specific, Not Generic

\`\`\`python
# Generic (might match unwanted text)
generic = r".+@.+\\..+"

# Specific (clearly defines valid characters)
specific = r"[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}"
\`\`\`

### 2. Use Word Boundaries

\`\`\`python
text = "The cat in the cathedral"

# Without boundaries - matches partial words
without = re.findall(r"cat", text)
print(f"Without boundaries: {without}")
# Output: ['cat', 'cat']  # Matches 'cat' in 'cathedral'

# With boundaries - matches whole words only
with_boundaries = re.findall(r"\\bcat\\b", text)
print(f"With boundaries: {with_boundaries}")
# Output: ['cat']  # Only the whole word
\`\`\`

## 🚀 What's Next?

You've mastered character classes and quantifiers! In Module 3, you'll learn:

- **Anchors**: ^ (start), $ (end) for position matching
- **Word Boundaries**: \\b for exact word matching  
- **Capturing Groups**: (pattern) to extract parts of matches
- **Non-capturing Groups**: (?:pattern) for grouping without capture
- **Backreferences**: Using captured content in patterns and replacements

These advanced features will give you surgical precision in pattern matching!`,

  exercises: [
    {
      id: 'module2-exercise1',
      title: 'Predefined Character Classes',
      description: 'Create a pattern using \\d+ to match one or more digits',
      type: 'regex',
      difficulty: 'easy',
      pattern: '\\d+',
      testCases: [
        { input: '123', expected: true, description: 'Multiple digits' },
        { input: '5', expected: true, description: 'Single digit' },
        { input: '0000', expected: true, description: 'Multiple zeros' },
        { input: 'abc', expected: false, description: 'No digits' },
        { input: 'a123b', expected: true, description: 'Contains digits in middle' },
        { input: 'Price: $29.99', expected: true, description: 'Multiple digit groups' },
      ],
      hints: [
        'Use \\d for any single digit (0-9)',
        'Use + for one or more occurrences',
        'The pattern \\d+ will match sequences of digits',
      ],
    },
    {
      id: 'module2-exercise2',
      title: 'Custom Character Classes',
      description: 'Create a pattern [aeiou]+ to match one or more vowels',
      type: 'regex',
      difficulty: 'easy',
      pattern: '[aeiou]+',
      testCases: [
        { input: 'hello', expected: true, description: 'Contains vowels e and o' },
        { input: 'aaa', expected: true, description: 'Multiple same vowels' },
        { input: 'beautiful', expected: true, description: 'Multiple vowel sequences' },
        { input: 'xyz', expected: false, description: 'No vowels' },
        { input: 'rhythm', expected: false, description: 'No vowels' },
        { input: 'quick', expected: true, description: 'Contains vowels u and i' },
      ],
      hints: [
        'Use square brackets [aeiou] to create a character class',
        'Add + to match one or more vowels in sequence',
        'This will match sequences like "ea", "ou", "a", etc.',
      ],
    },
    {
      id: 'module2-exercise3',
      title: 'Quantifiers Practice',
      description: 'Create a pattern for US ZIP codes: exactly 5 digits using \\d{5}',
      type: 'regex',
      difficulty: 'medium',
      pattern: '\\d{5}',
      testCases: [
        { input: '12345', expected: true, description: 'Valid 5-digit ZIP' },
        { input: '90210', expected: true, description: 'Famous ZIP code' },
        { input: '1234', expected: false, description: 'Only 4 digits' },
        { input: '123456', expected: true, description: 'Contains 5 digits (ZIP+4 format)' },
        { input: 'abcde', expected: false, description: 'Letters instead of digits' },
        { input: 'ZIP: 12345', expected: true, description: 'ZIP code in text' },
      ],
      hints: [
        'Use \\d for digits',
        'Use {5} to match exactly 5 occurrences',
        'The pattern \\d{5} matches exactly 5 consecutive digits',
      ],
    },
    {
      id: 'module2-exercise4',
      title: 'Negated Character Classes',
      description: 'Create a pattern [^0-9]+ to match one or more non-digit characters',
      type: 'regex',
      difficulty: 'medium',
      pattern: '[^0-9]+',
      testCases: [
        { input: 'hello', expected: true, description: 'All letters (no digits)' },
        { input: 'abc!@#', expected: true, description: 'Letters and symbols (no digits)' },
        { input: '123', expected: false, description: 'Only digits' },
        { input: 'abc123def', expected: true, description: 'Contains non-digit sequences' },
        { input: 'Price: $', expected: true, description: 'Text and symbols' },
        { input: '   ', expected: true, description: 'Spaces are not digits' },
      ],
      hints: [
        'Use [^0-9] to match any character that is NOT a digit',
        'The ^ inside brackets means "not" or "except"',
        'Add + to match one or more non-digit characters in sequence',
      ],
    },
    {
      id: 'module2-exercise5',
      title: 'Complex Pattern Building',
      description:
        'Create a basic email pattern: \\w+@\\w+\\.\\w+ (word chars + @ + domain + dot + extension)',
      type: 'regex',
      difficulty: 'hard',
      pattern: '\\w+@\\w+\\.\\w+',
      testCases: [
        { input: 'user@domain.com', expected: true, description: 'Standard email format' },
        { input: 'test@site.org', expected: true, description: 'Valid email with org domain' },
        { input: 'name123@company123.net', expected: true, description: 'Email with numbers' },
        { input: '@domain.com', expected: false, description: 'Missing username' },
        { input: 'user@.com', expected: false, description: 'Missing domain name' },
        { input: 'user@domain', expected: false, description: 'Missing extension' },
        {
          input: 'user.name@domain.com',
          expected: true,
          description: 'Contains valid email pattern',
        },
      ],
      hints: [
        'Use \\w+ for one or more word characters (letters, digits, underscore)',
        'Use @ literally for the @ symbol',
        'Use \\. to match a literal dot (escape the special character)',
        'Pattern structure: username@domain.extension',
      ],
    },
  ],

  quiz: {
    id: 'module2-quiz',
    title: 'Character Classes and Quantifiers Quiz',
    questions: [
      {
        id: 'q1',
        question: 'What does the \\d character class match?',
        choices: ['Any letter', 'Any digit (0-9)', 'Any whitespace character', 'The letter "d"'],
        correctAnswer: 1,
        explanation:
          '\\d is a predefined character class that matches any single digit from 0 to 9.',
      },
      {
        id: 'q2',
        question: 'What does the + quantifier mean in regex?',
        choices: [
          'Zero or more occurrences',
          'One or more occurrences',
          'Zero or one occurrence',
          'Exactly one occurrence',
        ],
        correctAnswer: 1,
        explanation:
          'The + quantifier matches one or more occurrences of the preceding character or group.',
      },
      {
        id: 'q3',
        question: 'What does [^abc] match?',
        choices: [
          'The letters a, b, or c',
          'Any character except a, b, or c',
          'The beginning of a string',
          'Any uppercase letter',
        ],
        correctAnswer: 1,
        explanation:
          '[^abc] is a negated character class that matches any character EXCEPT a, b, or c. The ^ inside brackets means "not".',
      },
      {
        id: 'q4',
        question: 'What does \\w represent in regex?',
        choices: [
          'Any whitespace character',
          'Any word character (letters, digits, underscore)',
          'Any alphabetic character only',
          'The letter "w"',
        ],
        correctAnswer: 1,
        explanation:
          '\\w matches word characters, which includes letters (a-z, A-Z), digits (0-9), and underscore (_).',
      },
      {
        id: 'q5',
        question: 'What does the pattern \\d{3,5} match?',
        choices: [
          'Exactly 3 or 5 digits',
          'Between 3 and 5 digits',
          '3 digits followed by 5 digits',
          'Any number of digits',
        ],
        correctAnswer: 1,
        explanation:
          '{3,5} is a range quantifier that matches between 3 and 5 occurrences of the preceding element (digits in this case).',
      },
      {
        id: 'q6',
        question: 'Which pattern correctly matches a literal dot character?',
        choices: ['.', '\\d', '\\.', '[.]'],
        correctAnswer: 2,
        explanation:
          'To match a literal dot, you need to escape it with a backslash: \\. The plain dot (.) matches any character, and [.] also matches a literal dot but \\. is more common.',
      },
    ],
    questionBank: regexModule2QuestionBank,
    questionsPerQuiz: 10,
    passingScore: 7,
  },

  resources: [
    {
      id: 'character-classes-guide',
      title: 'Character Classes Reference',
      type: 'document',
      url: '/resources/character-classes-guide.pdf',
      description: 'Comprehensive guide to predefined and custom character classes',
    },
    {
      id: 'quantifiers-cheatsheet',
      title: 'Quantifiers Cheat Sheet',
      type: 'document',
      url: '/resources/quantifiers-cheatsheet.pdf',
      description: 'Quick reference for all regex quantifiers with examples',
    },
    {
      id: 'regex-coach',
      title: 'The Regex Coach',
      type: 'link',
      url: 'http://www.weitz.de/regex-coach/',
      description: 'Interactive tool for testing and visualizing regex patterns',
    },
  ],
};
